import { useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LogoutModalContext } from '../contexts/LogoutModalContext';
import { TabProvider } from '../contexts/TabContext';
import { BusinessUnitProvider } from '../contexts/BusinessUnitContext';
import { PermissionsProvider } from '../contexts/PermissionsContext';
import useUser from '../hooks/useUser';
import routes from '../utils/routes';
import ProtectedRoute from './ProtectedRoute';
import SidebarNavigation from '../navigation/SidebarNavigation';
import Login from '../pages/authentication/Login';
import SetNewPassword from '../pages/authentication/SetNewPassword';
import ResetPassword from '../pages/authentication/ResetPassword';
import ChangePassword from '../pages/authentication/ChangePassword';
import LandingPage from '../pages/landing/LandingPage';
import BusinessUnit from '../pages/deals/BusinessUnit';
import Bank from '../pages/entities/Bank';
import Deal from '../pages/entities/Deal';
import DealPFI from '../pages/entities/DealPFI';
import Dropdowns from '../pages/entities/Dropdowns';
import FileType from '../pages/entities/FileType';
import Product from '../pages/entities/Product';
import SBLC from '../pages/entities/SBLC/SBLC_v2';
import Tenor from '../pages/entities/Tenor';
import Trader from '../pages/entities/Trader';
import CounterParty from '../pages/entities/CounterParty';
import DealReport from '../pages/reports/DealReport';
import FinanceReport from '../pages/reports/FinanceReport';
import MorningReport from '../pages/reports/MorningReport';
import ReportDocuments from '../pages/reports/ReportDocuments';
import ApproveDocuments from '../pages/documents/ApproveDocuments';
import MassUpdate from '../pages/batch/MassUpdate';
import NewKYC from '../pages/kyc/NewKYC';
import Requests from '../pages/kyc/Requests';
import EditKYC from '../pages/kyc/EditKYC';
import CounterPartyList from '../pages/kyc/CounterPartyList';
import FinanceAuditTrail from '../pages/audit/FinanceAuditTrail';
import UserAuditTrail from '../pages/audit/UserAuditTrail';
import UserManagement from '../pages/authentication/UserManagement';
import AddUser from '../pages/authentication/AddUser';
import EditProfile from '../pages/authentication/EditProfile';
import PageNotAccessible from '../PageNotAccessible';
import PageNotFound from '../PageNotFound';
import PeriodLockRequests from '../pages/periodLock/PeriodLockRequests';
import Settings from '../pages/periodLock/Settings';
import DocumentRequests from '../pages/documents/DocumentRequests';
import TreasuryReport from '../pages/reports/TreasuryReport';
import PreShipmentReport from '../pages/reports/PreShipmentReport';

const RouterLinks = () => 
{
    const { pathname } = useLocation();
    
    /* AUTHENTICATION VARIABLES */
    const currentUser = useUser();
    const { handleLogout } = useContext(LogoutModalContext);

    const isValidPath = () => 
    {
        return Object.values(routes).some(route => route === pathname);
    }

    return (
        <TabProvider>
            <BusinessUnitProvider logOut = {handleLogout}>
                <PermissionsProvider role = {currentUser?.role}>
                    <Routes>
                        <Route
                            path = "/login"
                            element = {<Login />}
                        />

                        <Route
                            path = "/set-new-password"
                            element = {
                                <SetNewPassword 
                                    currentUser = {currentUser}
                                    logOut = {handleLogout} 
                                />
                            }
                        />

                        <Route
                            path = "/reset-password"
                            element = {<ResetPassword currentUser = {currentUser} />}
                        />

                        <Route
                            path = "/change-password/user/:id"
                            element = {<ChangePassword currentUser = {currentUser} />}
                        />

                        <Route element = {<ProtectedRoute isAllowed = {!!currentUser} />}>
                           <Route
                                path = "/"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<LandingPage logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/fertilizers"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/foods"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/miscellaneous"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/petrochemicals/feedstocks"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/polymers"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/deals/sblc"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<BusinessUnit logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/banks"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Bank logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/counter-parties"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<CounterParty logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/deals"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Deal logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/deal-pfis"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<DealPFI logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/dropdowns"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Dropdowns logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/file-types"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<FileType logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/products"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Product logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/sblc"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<SBLC logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/tenors"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Tenor logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/entities/traders"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Trader logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/reports/deal-report"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<DealReport logOut = {handleLogout} />}
                                    />
                                }
                            />

                             <Route
                                path = "/reports/pre-shipment-report"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<PreShipmentReport logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/reports/finance-report"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<FinanceReport logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/reports/morning-report"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<MorningReport logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/reports/treasury-report"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<TreasuryReport logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/reports/report-documents/:trancheId/:fileType"
                                element = {
                                    <ReportDocuments logOut = {handleLogout} />
                                }
                            />

                            <Route
                                path = "/documents/approve-documents"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<ApproveDocuments logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/documents/approve-documents/:deal_no"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<ApproveDocuments logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/documents/document-requests"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<DocumentRequests logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/mass-update"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<MassUpdate logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/kyc/new-kyc"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<NewKYC logOut = {handleLogout} />}
                                    />
                                }
                            />

                             <Route
                                path = "/kyc/requests"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Requests logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/kyc/requests/:kyc_id"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Requests logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/kyc/edit-kyc"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<EditKYC logOut = {handleLogout} />}
                                    />
                                }
                            />
                            
                            <Route
                                path = "/kyc/counter-party-list"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<CounterPartyList logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/audit-trail/finance"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<FinanceAuditTrail logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/audit-trail/user"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<UserAuditTrail logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/period-lock/settings"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<Settings logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/period-lock/requests"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<PeriodLockRequests logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/user-management"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<UserManagement logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/user-management/add-user"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<AddUser logOut = {handleLogout} />}
                                    />
                                }
                            />

                            <Route
                                path = "/account/edit-profile"
                                element = {
                                    <SidebarNavigation
                                        logOut = {handleLogout}
                                        component = {<EditProfile logOut = {handleLogout} />}
                                    />
                                }
                            />
                        </Route>

                        <Route
                            path = "*"
                            element = {currentUser 
                                ? pathname !== "/" && isValidPath()
                                    ? <PageNotAccessible /> 
                                    : <PageNotFound /> 
                                : <Navigate to = "/login" replace = {true} />
                            }
                        />
                    </Routes>
                </PermissionsProvider>
            </BusinessUnitProvider>
        </TabProvider>
    );
}

export default RouterLinks;